import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { QrCode, Plus, Trash2, Download, Copy, MapPin, MoreVertical, Building2, Navigation, FileImage } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import QRPosterGenerator from '../components/QRPosterGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DevicesPage = () => {
  const [devices, setDevices] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [posterModalOpen, setPosterModalOpen] = useState(false);
  const [posterDevice, setPosterDevice] = useState(null);
  const [merchantLogo, setMerchantLogo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    campaign_id: '',
    location_name: '',
    location_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [devicesRes, campaignsRes, locationsRes, merchantRes] = await Promise.all([
        axios.get(`${API}/devices`),
        axios.get(`${API}/campaigns`),
        axios.get(`${API}/locations`),
        axios.get(`${API}/auth/me`)
      ]);
      setDevices(devicesRes.data);
      setCampaigns(campaignsRes.data);
      setLocations(locationsRes.data);
      setMerchantLogo(merchantRes.data.logo_url);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/devices`, formData);
      toast.success('Dispositivo QR creado');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating device:', error);
      toast.error(error.response?.data?.detail || 'Error al crear dispositivo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este dispositivo QR?')) return;
    try {
      await axios.delete(`${API}/devices/${id}`);
      toast.success('Dispositivo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', campaign_id: '', location_name: '', location_id: '' });
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || null;
  };

  const downloadQR = (device) => {
    const link = document.createElement('a');
    link.download = `qr-${device.name.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.href = `data:image/png;base64,${device.qr_code}`;
    link.click();
    toast.success('QR descargado');
  };

  const copyStampUrl = (device) => {
    const url = `${window.location.origin}/stamp/${device.id}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copiada al portapapeles');
  };

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign?.name || 'Campaña eliminada';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="devices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Códigos QR</h1>
          <p className="text-slate-600">Genera QR para que tus clientes registren visitas</p>
        </div>
        <Button 
          className="btn-primary"
          onClick={() => setDialogOpen(true)}
          disabled={campaigns.length === 0}
          data-testid="create-device-btn"
        >
          <Plus className="w-5 h-5" /> Nuevo QR
        </Button>
      </div>

      {/* Warning if no campaigns */}
      {campaigns.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800">
          <p className="font-medium">Primero crea una campaña</p>
          <p className="text-sm">Necesitas al menos una campaña activa para generar códigos QR</p>
        </div>
      )}

      {/* Devices Grid */}
      {devices.length === 0 ? (
        <motion.div 
          className="card-dashboard text-center py-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <QrCode className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Sin códigos QR</h3>
          <p className="text-slate-600 mb-6">Genera un código QR para colocarlo en tu punto de venta</p>
          {campaigns.length > 0 && (
            <Button 
              className="btn-primary"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="w-5 h-5" /> Crear QR
            </Button>
          )}
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {devices.map((device, i) => (
            <motion.div
              key={device.id}
              className="card-dashboard group hover:shadow-card-hover transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`device-card-${device.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-slate-900">{device.name}</h3>
                  <p className="text-sm text-slate-500">{getCampaignName(device.campaign_id)}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => downloadQR(device)}>
                      <Download className="w-4 h-4 mr-2" /> Descargar QR
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => copyStampUrl(device)}>
                      <Copy className="w-4 h-4 mr-2" /> Copiar URL
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setSelectedDevice(device)}>
                      <QrCode className="w-4 h-4 mr-2" /> Ver QR grande
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setPosterDevice(device); setPosterModalOpen(true); }}>
                      <FileImage className="w-4 h-4 mr-2" /> Generar Cartel A5
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-600"
                      onClick={() => handleDelete(device.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* QR Preview */}
              <div 
                className="bg-white rounded-xl p-4 border border-slate-100 flex items-center justify-center cursor-pointer hover:border-primary/50 transition-all"
                onClick={() => setSelectedDevice(device)}
              >
                <img 
                  src={`data:image/png;base64,${device.qr_code}`} 
                  alt={`QR ${device.name}`}
                  className="w-40 h-40"
                />
              </div>

              {device.location_name && (
                <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                  <MapPin className="w-4 h-4" />
                  <span>{device.location_name}</span>
                </div>
              )}

              {device.location_id && (
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="default" className="bg-green-100 text-green-700 text-xs">
                    <Navigation className="w-3 h-3 mr-1" />
                    Validación por ubicación: {getLocationName(device.location_id)}
                  </Badge>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => downloadQR(device)}
                >
                  <Download className="w-4 h-4 mr-1" /> QR
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => copyStampUrl(device)}
                >
                  <Copy className="w-4 h-4 mr-1" /> URL
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 btn-primary"
                  onClick={() => { setPosterDevice(device); setPosterModalOpen(true); }}
                  data-testid={`poster-btn-${device.id}`}
                >
                  <FileImage className="w-4 h-4 mr-1" /> Cartel
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo código QR</DialogTitle>
            <DialogDescription>
              Crea un QR para que los clientes registren sus visitas
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del dispositivo</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Caja principal"
                required
                data-testid="device-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Campaña asociada</Label>
              <Select
                value={formData.campaign_id}
                onValueChange={(value) => setFormData({ ...formData, campaign_id: value })}
                required
              >
                <SelectTrigger data-testid="device-campaign-select">
                  <SelectValue placeholder="Selecciona una campaña" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.filter(c => c.active).map(campaign => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Ubicación (opcional)</Label>
              <Input
                value={formData.location_name}
                onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                placeholder="Ej: Local Centro"
                data-testid="device-location-input"
              />
            </div>

            {/* Location Selection for Geofencing */}
            {locations.length > 0 && (
              <div className="space-y-2">
                <Label>Local para validación por ubicación</Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) => setFormData({ ...formData, location_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger data-testid="device-geolocation-select">
                    <SelectValue placeholder="Sin validación por ubicación" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin validación por ubicación</SelectItem>
                    {locations.map(location => (
                      <SelectItem key={location.id} value={location.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          {location.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Si seleccionas un local, los clientes deberán estar cerca para registrar su visita
                </p>
              </div>
            )}

            {locations.length === 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-700">
                <div className="flex items-start gap-2">
                  <Navigation className="w-4 h-4 mt-0.5" />
                  <div>
                    <p className="font-medium">Validación por geolocalización</p>
                    <p className="text-blue-600">Crea un local en la sección &quot;Locales&quot; para activar esta función</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="flex-1 btn-primary"
                data-testid="device-submit-btn"
              >
                Crear QR
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Preview Dialog */}
      <Dialog open={!!selectedDevice} onOpenChange={() => setSelectedDevice(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">{selectedDevice?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            {selectedDevice && (
              <img 
                src={`data:image/png;base64,${selectedDevice.qr_code}`} 
                alt={`QR ${selectedDevice.name}`}
                className="w-64 h-64"
              />
            )}
            <p className="text-sm text-slate-500 text-center">
              Escanea este código con tu cámara para registrar una visita
            </p>
            <div className="flex gap-2 w-full">
              <Button 
                className="flex-1" 
                variant="outline"
                onClick={() => copyStampUrl(selectedDevice)}
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar URL
              </Button>
              <Button 
                className="flex-1 btn-primary" 
                onClick={() => downloadQR(selectedDevice)}
              >
                <Download className="w-4 h-4 mr-2" /> Descargar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Poster Generator Modal */}
      <QRPosterGenerator
        isOpen={posterModalOpen}
        onClose={() => { setPosterModalOpen(false); setPosterDevice(null); }}
        device={posterDevice}
        merchantLogo={merchantLogo}
        qrCodeData={posterDevice ? `data:image/png;base64,${posterDevice.qr_code}` : null}
      />
    </div>
  );
};

export default DevicesPage;
